import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.infinitagents.com';

/** https://api.infinitagents.com
 * Axios client configuration
 */
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  }
});

/**
 * Initial axios setup to include authentication tokens
 * @param {Object} options - Configuration options
 * @param {string} options.tokenKey - localStorage key for token (default: 'authToken')
 * @param {string} options.userDataKey - localStorage key for user data (default: 'userData')
 * @param {string} options.loginPath - Login redirect path (default: '/login')
 */
export const setupAxiosInterceptors = (options = {}) => {
  const {
    tokenKey = 'authToken',
    userDataKey = 'userData',
    loginPath = '/login'
  } = options;

  // Request interceptor - adds token
  apiClient.interceptors.request.use(
    config => {
      const token = localStorage.getItem(tokenKey);

      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
      return config;
    },
    error => Promise.reject(error)
  );

  // Response interceptor - handles 401 errors
  apiClient.interceptors.response.use(
    response => response,
    error => {
      if (error.response?.status === 401) {
        // Expired or invalid token
        logoutUser({ tokenKey, userDataKey });
        window.location.href = loginPath;
      }
      return Promise.reject(error);
    }
  );
};

/**
 * Checks if user is authenticated
 * @param {string} tokenKey - localStorage key for token
 * @returns {boolean} True if user is authenticated
 */
export const isAuthenticated = (tokenKey = 'authToken') => {
  return !!localStorage.getItem(tokenKey);
};

/**
 * Gets current user data
 * @param {string} userDataKey - localStorage key for user data
 * @returns {Object|null} User data or null
 */
export const getCurrentUser = (userDataKey = 'userData') => {
  try {
    const userDataString = localStorage.getItem(userDataKey);
    return userDataString ? JSON.parse(userDataString) : null;
  } catch (error) {
    console.error('Error getting user data:', error);
    return null;
  }
};

/**
 * Logs out the user
 * @param {Object} options - Configuration options
 */
export const logoutUser = (options = {}) => {
  const {
    tokenKey = 'authToken',
    userDataKey = 'userData'
  } = options;
  
  localStorage.removeItem(tokenKey);
  localStorage.removeItem(userDataKey);
};

/**
 * Updates user data in localStorage
 * @param {Object} userData - New user data
 * @param {string} userDataKey - localStorage key
 */
export const updateUserData = (userData, userDataKey = 'userData') => {
  localStorage.setItem(userDataKey, JSON.stringify(userData));
};

/**
 * Configured API client for use throughout the application
 */
export { apiClient };

/**
 * HTTP request helpers
 */
export const api = {
  get: (url, config) => apiClient.get(url, config),
  post: (url, data, config) => apiClient.post(url, data, config),
  put: (url, data, config) => apiClient.put(url, data, config),
  patch: (url, data, config) => apiClient.patch(url, data, config),
  delete: (url, config) => apiClient.delete(url, config)
};

/**
 * Custom hook for request state management
 * @example
 * const { data, loading, error, execute } = createApiRequest();
 */
export const createApiRequest = () => {
  let state = {
    loading: false,
    error: null,
    data: null
  };

  const execute = async (requestFn) => {
    state.loading = true;
    state.error = null;
    
    try {
      const response = await requestFn();
      state.data = response.data;
      return response.data;
    } catch (error) {
      state.error = error;
      throw error;
    } finally {
      state.loading = false;
    }
  };

  return { ...state, execute };
};

export default {
  setupAxiosInterceptors,
  isAuthenticated,
  getCurrentUser,
  logoutUser,
  updateUserData,
  api,
  apiClient,
  createApiRequest
};